import type {
  CredentialStatus,
  VerifiedCredential,
} from '@seek/ca-myprofile-utils';
import { Licence } from '@seek/profile-read-ui';
import { useTranslations } from '@vocab/react';
import { Actions, Button, Heading, Stack, Text } from 'braid-design-system';
import { useEffect } from 'react';
import { v4 as uuid } from 'uuid';

import type {
  Licence as LicenceType,
  MonthYear,
} from 'src/__generated__/graphql';
import { useLicenceTracking } from 'src/components/Licences/hooks/tracking';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

import translations from './.vocab';

interface Props {
  licences: LicenceType[];
  hideWhenEditing?: boolean;
  shouldShowHeader?: boolean;
}

export function LicencesList({
  licences,
  hideWhenEditing = false,
  shouldShowHeader = false,
}: Props) {
  const { t } = useTranslations(translations);
  const { trackViewList } = useLicenceTracking();

  const { onEditorOpen, editingId } = useEditorDrawer();

  useEffect(() => {
    trackViewList({ licences });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (hideWhenEditing && editingId) {
    return null;
  }

  return (
    <Stack space="large">
      {shouldShowHeader && (
        <Heading level="3">{t('Licences certifications')}</Heading>
      )}
      {licences.length === 0 && (
        <Text tone="secondary">
          {t(
            'Showcase your professional credentials Add your relevant licences certificates memberships and accreditations here',
          )}
        </Text>
      )}
      <Stack component="ul" space="small">
        {licences.map((licence) => (
          <li key={licence.id}>
            <Licence
              id={licence.id}
              // @ts-expect-error ontologyId are incompatible.
              name={licence.name}
              noExpiryDate={licence.noExpiryDate}
              issuingOrganisation={licence.issuingOrganisation as string}
              issueDate={licence.issueDate as MonthYear}
              expiryDate={licence.expiryDate as MonthYear}
              description={licence.description as string}
              credential={licence.credential as VerifiedCredential}
              status={licence.status as unknown as CredentialStatus}
              formattedDate={licence.formattedDate as string}
              isVerified={licence.isVerified}
              verificationUrl={licence.verificationUrl as string}
              verificationMessage={licence.verificationMessage as string}
              // @ts-expect-error
              credentialType={licence.credentialType}
              displayVariant="card"
              editLicence={(id) => onEditorOpen(id)}
            />
          </li>
        ))}
      </Stack>
      <Actions>
        <Button
          tone="formAccent"
          variant="ghost"
          onClick={() => onEditorOpen(uuid())}
          data={{ automation: 'add-licence-button' }}
        >
          {t('Add licence or certification')}
        </Button>
      </Actions>
    </Stack>
  );
}
